import React, { useRef, useMemo, useEffect } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";

// Single component that renders ALL bugs using 3 InstancedMeshes (body + 2 wings)
// instead of N×3 individual meshes — reduces draw calls from N×3 to 3.
export default function FlyingBugs({ trajectories }) {
  const bodyRef = useRef();
  const leftWingRef = useRef();
  const rightWingRef = useRef();
  const count = trajectories.length;

  // Bounding sphere covering all reachable instance positions.
  // Trajectory math: |x| ≤ |baseX| + |ampX|, etc. Max amp is terrainHalf (150)
  // for edge bugs. Add a small margin.
  const boundingSphere = useMemo(() => {
    let maxR = 0;
    for (const t of trajectories) {
      const r = Math.hypot(
        Math.abs(t.baseX) + t.ampX,
        Math.abs(t.baseY) + t.ampY,
        Math.abs(t.baseZ) + t.ampZ,
      );
      if (r > maxR) maxR = r;
    }
    return new THREE.Sphere(new THREE.Vector3(0, 0, 0), maxR + 5);
  }, [trajectories]);

  useEffect(() => {
    for (const ref of [bodyRef, leftWingRef, rightWingRef]) {
      if (ref.current) ref.current.boundingSphere = boundingSphere;
    }
  }, [boundingSphere]);

  const bodyGeometry = useMemo(() => new THREE.SphereGeometry(0.05, 6, 6), []);
  const wingGeometry = useMemo(() => new THREE.CircleGeometry(0.04, 8), []);
  const bodyMaterial = useMemo(
    () => new THREE.MeshStandardMaterial({ color: "black" }),
    [],
  );
  const wingMaterial = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: "white",
        transparent: true,
        opacity: 0.7,
      }),
    [],
  );

  // Reusable objects — allocated once, mutated each frame
  const parentObj = useMemo(() => new THREE.Object3D(), []);
  const childObj = useMemo(() => new THREE.Object3D(), []);
  const tempMatrix = useMemo(() => new THREE.Matrix4(), []);

  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    const wingFlap = Math.sin(t * 20) * 0.6;

    for (let i = 0; i < count; i++) {
      const traj = trajectories[i];
      const x =
        traj.baseX + Math.sin(t * traj.speedX + traj.phaseX) * traj.ampX;
      const y =
        traj.baseY + Math.sin(t * traj.speedY + traj.phaseY) * traj.ampY;
      const z =
        traj.baseZ + Math.cos(t * traj.speedZ + traj.phaseZ) * traj.ampZ;

      // Parent transform (shared by body + wings)
      parentObj.position.set(x, y, z);
      parentObj.rotation.set(0, t * 2, 0);
      parentObj.scale.set(1, 1, 1);
      parentObj.updateMatrix();

      // Body
      childObj.position.set(0, 0, 0);
      childObj.rotation.set(0, 0, 0);
      childObj.scale.set(0.4, 0.4, 0.4);
      childObj.updateMatrix();
      tempMatrix.multiplyMatrices(parentObj.matrix, childObj.matrix);
      bodyRef.current.setMatrixAt(i, tempMatrix);

      // Left wing
      childObj.position.set(-0.06, 0, 0);
      childObj.rotation.set(0, 0, wingFlap);
      childObj.scale.set(0.7, 0.28, 1);
      childObj.updateMatrix();
      tempMatrix.multiplyMatrices(parentObj.matrix, childObj.matrix);
      leftWingRef.current.setMatrixAt(i, tempMatrix);

      // Right wing
      childObj.position.set(0.06, 0, 0);
      childObj.rotation.set(0, 0, -wingFlap);
      childObj.scale.set(0.7, 0.28, 1);
      childObj.updateMatrix();
      tempMatrix.multiplyMatrices(parentObj.matrix, childObj.matrix);
      rightWingRef.current.setMatrixAt(i, tempMatrix);
    }

    bodyRef.current.instanceMatrix.needsUpdate = true;
    leftWingRef.current.instanceMatrix.needsUpdate = true;
    rightWingRef.current.instanceMatrix.needsUpdate = true;
  });

  return (
    <>
      <instancedMesh
        ref={bodyRef}
        args={[bodyGeometry, bodyMaterial, count]}
      />
      <instancedMesh
        ref={leftWingRef}
        args={[wingGeometry, wingMaterial, count]}
      />
      <instancedMesh
        ref={rightWingRef}
        args={[wingGeometry, wingMaterial, count]}
      />
    </>
  );
}
